import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Input from "@/components/ui/Input";
import Textarea from "@/components/ui/Textarea";
import Button from "@/components/ui/Button";
import PriceBreakdown from "./PriceBreakdown";
import { GuestDetailsSchema, type GuestDetails } from "@/types/booking";
import type { BookingDraft } from "@/types/booking";

interface StepGuestDetailsProps {
  draft: BookingDraft;
  onNext: (guest: GuestDetails) => void;
  onBack: () => void;
}

export default function StepGuestDetails({
  draft,
  onNext,
  onBack,
}: StepGuestDetailsProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GuestDetails>({
    resolver: zodResolver(GuestDetailsSchema),
    defaultValues: draft.guest ?? {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      specialRequests: "",
    },
  });

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
      {/* Left — form */}
      <form onSubmit={handleSubmit(onNext)} noValidate className="space-y-6">
        <div>
          <h2 className="font-serif text-2xl text-metro-green-950 mb-1">
            Your details
          </h2>
          <p className="text-sm text-metro-ink-subtle">
            We'll use this information to confirm your booking.
          </p>
        </div>

        <section className="space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
            Personal Information
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First Name"
              required
              placeholder="Rahul"
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <Input
              label="Last Name"
              required
              placeholder="Sharma"
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Email"
              type="email"
              required
              placeholder="rahul@example.com"
              hint="Booking confirmation will be sent here."
              error={errors.email?.message}
              {...register("email")}
            />
            <Input
              label="Phone"
              type="tel"
              required
              placeholder="+91 98765 43210"
              error={errors.phone?.message}
              {...register("phone")}
            />
          </div>
          <Input
            label="Address"
            placeholder="123, MG Road, Bangalore"
            error={errors.address?.message}
            {...register("address")}
          />
        </section>

        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-metro-ink-subtle">
            Special Requests
          </h3>
          <Textarea
            rows={3}
            placeholder="Any special requests? (early check-in, dietary requirements, etc.)"
            hint="We'll do our best to accommodate your requests."
            {...register("specialRequests")}
          />
        </section>

        <div className="flex justify-between border-t border-gray-100 pt-4">
          <Button
            variant="outline"
            type="button"
            leftIcon={<ArrowLeft size={16} />}
            onClick={onBack}
          >
            Back
          </Button>
          <Button type="submit" rightIcon={<ArrowRight size={16} />}>
            Continue to Payment
          </Button>
        </div>
      </form>

      {/* Right — price summary including add-ons */}
      {draft.pricing && (
        <div className="hidden lg:block">
          <div className="sticky top-24">
            <PriceBreakdown
              pricing={draft.pricing}
              roomTypeName={draft.roomTypeName}
              checkIn={draft.checkIn}
              checkOut={draft.checkOut}
              addons={draft.addons}
              addonsTotal={draft.addonsTotal}
            />
          </div>
        </div>
      )}
    </div>
  );
}